import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { RecoilRoot } from 'recoil';
import FilterBar from './components/FilterBar';
import dayjs from 'dayjs';
import useGetFlexHistoryValues from './hooks/useGetFlexHistoryValues';
import { useGetColumns } from './hooks/useGetColumns';
import { FlexHistoryData } from './types';
import { faLinkSlash } from 'vendors/@fortawesome/pro-solid-svg-icons';
import { t } from 'i18next';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import DisconnectConfirmation from './components/DisconnectConfirmation';
import { useSelector } from 'react-redux';
import { RootState } from 'config/reduxStore';
import { DetailsDialog } from './components/DetailsDialog';

type FilterStateTypes = {
  costCenterId: number[] | null;
  startDate: string | null;
  endDate: string | null;
  clusterId: number | null;
  poolId: number | null;
  employeeId: number | null;
  requestReason: string | null;
  functionId: number | null;
  agencyId: number[] | null;
};

const FlexHistory: FC = () => {
  const columns = useGetColumns();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const hasPoolsOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasPoolsOrgGrant
  );
  const hasClustersOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasClustersOrgGrant
  );

  const [filterStates, setFilterStates] = useState<FilterStateTypes>({
    costCenterId: null,
    startDate: dayjs(new Date()).startOf('month').toISOString(),
    endDate: dayjs(new Date()).endOf('month').toISOString(),
    clusterId: null,
    poolId: null,
    employeeId: null,
    requestReason: null,
    functionId: null,
    agencyId: null
  });
  const startDate = dayjs(filterStates.startDate).isValid()
    ? dayjs(filterStates.startDate).toISOString()
    : dayjs(new Date()).startOf('month').toISOString();

  const endDate = dayjs(filterStates.endDate).isValid()
    ? dayjs(filterStates.endDate).toISOString()
    : dayjs(new Date()).endOf('month').toISOString();

  const {
    data: { data: rows = [], totalItems = 0 } = {},
    isFetching,
    refetch
  } = useGetFlexHistoryValues(
    startDate,
    endDate,
    filterStates.costCenterId ? filterStates.costCenterId : [],
    hasClustersOrgGrant ? filterStates?.clusterId : null,
    hasPoolsOrgGrant ? filterStates?.poolId : null,
    filterStates?.employeeId,
    filterStates?.requestReason,
    filterStates?.functionId,
    filterStates?.agencyId,
    page,
    pageSize
  );

  const isMounted = useRef(false);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const [filteredRows, setFilteredRows] = useState<FlexHistoryData[]>([]);

  const handleSearch = useCallback(() => {
    setPage(0);
    refetch();
  }, []);

  useEffect(() => {
    setFilteredRows(rows || []);
  }, [isFetching, page, pageSize]);

  useEffect(() => {
    isMounted.current ? refetch() : (isMounted.current = true);
  }, [page, pageSize]);

  const handleFiltersChange = (filters) => {
    setFilterStates((prevState) => ({
      ...prevState,
      ...filters
    }));
  };

  const [showDisconnectConfirmation, setShowDisconnectConfirmation] =
    useState(false);
  const [rowIdToDisconnect, setRowIdToDisconnect] = useState<
    number | string | null
  >(null);

  const handleDisconnect = (id: string | number) => {
    setRowIdToDisconnect(id);
    setShowDisconnectConfirmation(true);
  };

  const hideActions = filteredRows.every((row) => row.deleteReason);

  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FlexHistoryData | null>(null);

  return (
    <>
      <Box p={3.5}>
        <FilterBar
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          hasPoolsOrgGrant={hasPoolsOrgGrant}
          hasClustersOrgGrant={hasClustersOrgGrant}
        />
        <TableCRUDLayout
          rows={filteredRows || []}
          columns={columns}
          loading={isFetching}
          autoHeight={true}
          isContentRangePagination={true}
          pageSize={pageSize}
          editable={false}
          paginationMode="server"
          rowCount={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          page={page}
          onViewClick={(id, row) => {
            setSelectedRow(row);
            setShowNotesDialog(true);
          }}
          disableActions={hideActions}
          FormComponent={() => null}
          customActions={(id, row) => [
            {
              tooltipTitle: t('flexHistory.disconnect'),
              icon: faLinkSlash,
              onClick: () => handleDisconnect(id as string),
              hidden: row.deleteReason
            }
          ]}
        />
      </Box>
      {showDisconnectConfirmation && (
        <DisconnectConfirmation
          rowIdToDisconnect={rowIdToDisconnect}
          setShowDisconnectConfirmation={setShowDisconnectConfirmation}
        />
      )}
      {showNotesDialog && (
        <DetailsDialog
          open={showNotesDialog}
          onClose={() => setShowNotesDialog(false)}
          row={selectedRow}
        />
      )}
    </>
  );
};

export default () => (
  <RecoilRoot>
    <FlexHistory />
  </RecoilRoot>
);
